/**
 * TK-38 việc 4 (lee 23/10) — CÂY Tuyến → Tủ → Đèn của trang Quản lý đèn. Sổ xuống, tìm theo tên tuyến / tủ / đèn / mã đèn
 * (mã đèn chỉ dùng để TÌM, không hiện trên cây — quy tắc 22/10). Dòng tủ: sáng/tổng; dòng đèn: chấm trạng thái (cùng bảng màu
 * với bản đồ — `MAU_BADGE`). Chọn mục ⇒ `onChon` (trang ghi vào URL `?tuyen=&tu=&den=`).
 */
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { MAU_BADGE, NHAN_BADGE, tenDen } from '../../lib/nemaNhan';
import type { TuyenCay } from '../../lib/nemaApi';
import { duongMo, locCay } from '../../lib/vanHanh';

export interface ChonCay { tuyen: string | null; tu: string | null; den: number | null }
export function CayDen({ cay, chon, onChon }: { cay: TuyenCay[]; chon: ChonCay; onChon: (c: ChonCay) => void }) {
  const [q, setQ] = useState('');
  /* TK-41 §3: tập ĐANG MỞ (mặc định rỗng = thu gọn); mục trên URL ⇒ mở đúng đường dẫn — một lần, khi cây có dữ liệu */
  const [moTap, setMoTap] = useState<Set<string>>(() => duongMo(cay, chon));
  const daMoUrl = cay.length > 0;
  useEffect(() => { if (daMoUrl) setMoTap((s) => (s.size ? s : duongMo(cay, chon))); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [daMoUrl]);
  const ds = useMemo(() => locCay(cay, q), [cay, q]);
  const dangTim = q.trim().length > 0;
  const mo = (k: string) => dangTim || moTap.has(k);
  const doi = (k: string) => setMoTap((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const bung = (k: string) => setMoTap((s) => (s.has(k) ? s : new Set(s).add(k)));
  const lopDong = (sel: boolean) => `flex w-full min-w-0 items-center gap-1.5 rounded-nut px-2 py-1.5 text-left text-sm hover:bg-nen-2 ${sel ? 'bg-nen-2 ring-1 ring-nhan' : ''}`;
  return (
    <section className="flex min-h-0 flex-col rounded-the border border-vien bg-nen-1" data-o="cay-den" aria-label="Cây tuyến, tủ, đèn">
      <div className="border-b border-vien p-2">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm tuyến, tủ, đèn" aria-label="Tìm tuyến, tủ, đèn"
          className="w-full rounded-nut border border-vien bg-nen-lom px-3 py-2 text-sm text-chu-1 placeholder:text-chu-3" />
      </div>
      <ul className="m-0 min-h-0 list-none overflow-y-auto p-1 lg:max-h-[calc(100vh-220px)]" role="tree">
        {ds.map((r) => (
          <li key={r.id} role="treeitem" aria-expanded={mo(r.id)}>
            <div className="flex items-center">
              <button type="button" onClick={() => doi(r.id)} aria-label={mo(r.id) ? 'Thu' : 'Mở'} className="grid h-8 w-6 shrink-0 place-items-center text-chu-3">
                {mo(r.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              <button type="button" className={lopDong(chon.tuyen === r.id && !chon.tu)} onClick={() => { bung(r.id); onChon({ tuyen: r.id, tu: null, den: null }); }} data-cay-tuyen={r.id}>
                <span className="min-w-0 flex-1 truncate font-semibold text-chu-1">{r.tenDayDu}</span>
                <span className="shrink-0 text-xs tabular-nums text-chu-3">{r.tu.length} tủ</span>
              </button>
            </div>
            {mo(r.id) && (
              <ul className="m-0 list-none pl-5" role="group">
                {r.tu.map((t) => (
                  <li key={t.id} role="treeitem" aria-expanded={mo(t.id)}>
                    <div className="flex items-center">
                      {/* TK-44 §1: tủ chưa có bảng đèn đã lưu ⇒ không có cấp con (không nút bung) */}
                      {t.chuaCai ? <span className="w-6 shrink-0" aria-hidden="true" /> : (
                        <button type="button" onClick={() => doi(t.id)} aria-label={mo(t.id) ? 'Thu' : 'Mở'} className="grid h-8 w-6 shrink-0 place-items-center text-chu-3">
                          {mo(t.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      )}
                      <button type="button" className={lopDong(chon.tu === t.id && chon.den === null)} onClick={() => { bung(t.id); onChon({ tuyen: r.id, tu: t.id, den: null }); }} data-cay-tu={t.id}>
                        <span className={`h-2 w-2 shrink-0 rounded-full ${t.online ? 'bg-tot' : 'bg-chu-3'}`} aria-hidden="true" />
                        <span className="min-w-0 flex-1 truncate text-chu-1">{t.name}</span>
                        {t.chuaCai
                          ? <span className="shrink-0 rounded-full border border-vien px-2 text-xs text-chu-3" data-chip-chua-cai>Chưa cài</span>
                          : <span className="shrink-0 text-xs tabular-nums text-chu-2" title="đèn sáng / tổng">{t.online ? `${t.sang}/${t.soDen}` : `—/${t.soDen}`}</span>}
                      </button>
                    </div>
                    {mo(t.id) && !t.chuaCai && (
                      <ul className="m-0 list-none pl-7" role="group">
                        {t.den.map((d) => (
                          <li key={d.addr} role="treeitem">
                            <button type="button" className={lopDong(chon.tu === t.id && chon.den === d.addr)} onClick={() => onChon({ tuyen: r.id, tu: t.id, den: d.addr })} data-cay-den={`${t.id}:${d.addr}`}>
                              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: MAU_BADGE[d.badge] }} title={NHAN_BADGE[d.badge].nhan} data-badge={d.badge} />
                              <span className="min-w-0 flex-1 truncate text-chu-1">{tenDen(d)}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
        {ds.length === 0 && <li className="px-3 py-4 text-center text-sm text-chu-3">Không có kết quả</li>}
      </ul>
    </section>
  );
}
